import * as React from "react";
import { ApolloError, useQuery, useMutation, Types } from "@grandvisionhq/graphql";
import { useSelector } from "@grandvisionhq/state";
import { getAuth } from "../../../../selectors.js";
import { isLoggedIn } from "../../../../state.js";
import { toOptIns } from "../../../mappers/OptIns/mappers/opt-ins-mapper.js";
import UPDATE_OPT_INS from "../../../operations/mutations/OptIns/operations/mutations/update-opt-ins.js";
import GET_OPT_INS from "../../../operations/queries/OptIns/operations/queries/get-opt-ins.js";
import { useAvailableOptIns } from "./use-available-opt-ins.js";
const useOptIns = ({ onError, onCompleted } = {}) => {
  const auth = useSelector(getAuth);
  const [optInsFromServer, setOptInsFromServer] = React.useState([]);
  let authError = void 0;
  const { availableOptIns } = useAvailableOptIns();
  if (!isLoggedIn(auth)) {
    authError = new ApolloError({
      errorMessage: `Can't fetch opt-ins when user isn't logged in`
    });
    onError?.({
      reason: "AUTH",
      error: authError
    });
  }
  const handleQueryError = React.useCallback(
    (error) => {
      onError?.({ reason: "QUERY", error });
    },
    [onError]
  );
  const handleMutationError = React.useCallback(
    (error) => {
      onError?.({ reason: "MUTATION", error });
    },
    [onError]
  );
  const queryOptions = React.useMemo(
    () => ({
      skip: Boolean(authError),
      onError: handleQueryError,
      onCompleted: (data) => {
        setOptInsFromServer(toOptIns(data.me?.optIns));
        onCompleted?.("QUERY");
      }
    }),
    [authError, handleQueryError, onCompleted]
  );
  const query = useQuery(GET_OPT_INS, queryOptions);
  const mutationOptions = React.useMemo(
    () => ({
      skip: Boolean(authError),
      onError: handleMutationError,
      onCompleted: (data) => {
        setOptInsFromServer(toOptIns(data.updateB2COptIns?.optIns));
        onCompleted?.("MUTATION");
      }
    }),
    [authError, handleMutationError, onCompleted]
  );
  const [mutationCallback, mutation] = useMutation(UPDATE_OPT_INS, mutationOptions);
  const updateOptIns = React.useCallback(
    async (changedOptIns) => {
      if (!changedOptIns.length) {
        onCompleted?.("MUTATION");
        return;
      }
      const updatedOptIns = changedOptIns.map((optIn) => ({
        ...optIn,
        uiType: Types.UIType.CHECKBOX
      }));
      await mutationCallback({
        variables: {
          customerId: auth.userId,
          optinUpdates: updatedOptIns
        }
      });
    },
    [auth.userId, mutationCallback, onCompleted]
  );
  return React.useMemo(
    () => ({
      optInsFromServer,
      availableOptIns,
      updateOptIns,
      isFetching: query.loading,
      fetchError: authError || query.error,
      isUpdating: mutation.loading,
      updateError: mutation.error
    }),
    [
      authError,
      availableOptIns,
      mutation.error,
      mutation.loading,
      optInsFromServer,
      query.error,
      query.loading,
      updateOptIns
    ]
  );
};
export {
  useOptIns
};
//# sourceMappingURL=use-opt-ins.js.map
